import { lazy, type ComponentType } from 'react';

const SESSION_KEY = 'crm-chunk-reload';
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Detects errors thrown when a dynamically imported chunk fails to load.
 * This typically happens after a deploy: the running HTML still references
 * old chunk hashes that no longer exist on the server.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: unknown } | null)?.message ?? err ?? '');
  const name = String((err as { name?: unknown } | null)?.name ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed') ||
    name === 'ChunkLoadError'
  );
}

/**
 * Reloads the page if a chunk-load error is detected, but throttles via
 * sessionStorage to avoid an infinite reload loop when the chunk is truly
 * missing.
 *
 * Returns true when a reload was triggered (caller should stop further
 * handling), false otherwise.
 */
export function reloadOnChunkError(err: unknown): boolean {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(err)) return false;

  try {
    const last = Number(window.sessionStorage.getItem(SESSION_KEY) || 0);
    if (Date.now() - last > RELOAD_COOLDOWN_MS) {
      window.sessionStorage.setItem(SESSION_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage may be blocked (private mode, etc.) — fall back to a
    // single reload attempt without throttling.
    window.location.reload();
    return true;
  }
  return false;
}

/**
 * Drop-in replacement for React.lazy that auto-reloads the page when the
 * dynamic import fails because of a stale/missing chunk (post-deploy).
 *
 * Use this for every code-split route. If the reload was already attempted
 * recently, the original error is rethrown so the ErrorBoundary can render a
 * fallback instead of looping.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (reloadOnChunkError(err)) {
        // The page is reloading; return a never-resolving promise so React
        // does not try to render anything in the meantime.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
